import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { SelfieImage } from "@/components/SelfieImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { readFileAsDataURL } from "@/lib/month";
import {
  Plus, Trash2, ChevronLeft, ChevronRight, ListChecks, Camera, Loader2, Flag, Clock, X, Check, Upload, Images, ShieldCheck, Pencil, Copy, Lightbulb, ArrowRightToLine,
} from "lucide-react";

const COLUMNS = [
  { key: "todo", label: "To Do", tone: "border-slate-300", dark: true },
  { key: "in_progress", label: "In Progress", tone: "border-amber-400" },
  { key: "done", label: "Done", tone: "border-emerald-400" },
];

const PRIORITY = {
  low: { label: "Low", cls: "bg-slate-100 text-slate-600" },
  medium: { label: "Medium", cls: "bg-sky-100 text-sky-700" },
  high: { label: "High", cls: "bg-rose-100 text-rose-700" },
};

const TASK_SOURCES = [
  { id: "agoda", label: "Agoda" },
  { id: "booking", label: "Booking" },
  { id: "expedia", label: "Expedia" },
  { id: "tiket", label: "Tiket" },
  { id: "traveloka", label: "Traveloka" },
  { id: "airbnb", label: "Airbnb" },
  { id: "others", label: "Others" },
];

const sourceLabel = (id) => TASK_SOURCES.find((s) => s.id === id)?.label || id;

function ChecklistRow({ item, onToggle, meDone }) {
  const checked = meDone !== undefined ? meDone : item.done;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const complete = async (proof = null) => {
    setBusy(true);
    await onToggle(item.id, !checked, proof);
    setBusy(false);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const data = await readFileAsDataURL(f);
    await complete(data);
  };

  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0" data-testid={`checklist-item-${item.id}`}>
      <button
        onClick={() => {
          if (!checked && item.requires_proof && !item.proof_path) {
            fileRef.current?.click();
          } else {
            complete();
          }
        }}
        disabled={busy}
        className={`mt-0.5 h-5 w-5 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 hover:border-indigo-400"}`}
        data-testid={`checklist-toggle-${item.id}`}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : checked && <Check className="h-3.5 w-3.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${checked ? "line-through text-slate-400" : "text-slate-700"}`}>{item.text}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {item.requires_proof && (
            <Badge variant="secondary" className="text-[10px] gap-1"><Camera className="h-2.5 w-2.5" /> Screenshot required</Badge>
          )}
          {item.proof_path && <SelfieImage path={item.proof_path} className="h-8 w-8 rounded object-cover ring-1 ring-slate-200" />}
          {item.proof_validated && !item.proof_path && (
            <Badge className="text-[10px] gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`checklist-validated-${item.id}`}>
              <ShieldCheck className="h-2.5 w-2.5" /> Validated
            </Badge>
          )}
          {item.done_by && Object.keys(item.done_by).length > 0 && (
            <span className="flex flex-wrap gap-1">
              {Object.values(item.done_by).map((d, i) => (
                <Badge key={i} className="text-[10px] gap-1 bg-teal-100 text-teal-700 hover:bg-teal-100" data-testid={`doneby-${item.id}-${i}`}>
                  <Check className="h-2.5 w-2.5" /> {d.name}{d.property_name ? ` · ${d.property_name}` : ""}
                </Badge>
              ))}
            </span>
          )}
        </div>
      </div>
      {!checked && (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="mt-0.5 text-slate-400 hover:text-indigo-500 flex-shrink-0"
          title="Complete with photo proof"
          data-testid={`checklist-photo-${item.id}`}
        >
          <Camera className="h-4 w-4" />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`checklist-proof-input-${item.id}`} />
    </div>
  );
}

function TaskDialog({ task, isAdmin, onStatus, onChecklist, onDelete, onValidate, myUid, staff, properties, onUpdated, canCreate, onDuplicated }) {
  const isShared = !task.property_id;
  const done = (task.checklist || []).filter((c) => (isShared && myUid ? Boolean((c.done_by || {})[myUid]) : c.done)).length;
  const completers = isShared
    ? Object.values((task.checklist || []).reduce((acc, c) => ({ ...acc, ...(c.done_by || {}) }), {}))
    : [];
  const total = (task.checklist || []).length;
  const idx = COLUMNS.findIndex((c) => c.key === task.status);
  const proofItems = (task.checklist || []).filter((c) => c.proof_path);
  const [preview, setPreview] = useState(null);
  const [confirmValidate, setConfirmValidate] = useState(false);
  const [validating, setValidating] = useState(false);

  const validate = async () => {
    setValidating(true);
    await onValidate(task.id);
    setValidating(false);
    setConfirmValidate(false);
  };

  return (
    <>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={PRIORITY[task.priority]?.cls || PRIORITY.medium.cls}>{PRIORITY[task.priority]?.label}</Badge>
          {task.source && <Badge variant="outline" data-testid="task-source-badge">{sourceLabel(task.source)}</Badge>}
          {isAdmin && <Badge variant="outline">{task.assignee_name}</Badge>}
        </div>
        <DialogTitle className="mt-1">{task.title}</DialogTitle>
        <DialogDescription>{task.description || "No description provided."}</DialogDescription>
      </DialogHeader>

      {task.due_date && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> Due {new Date(task.due_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Status:</span>
        <Button size="sm" variant="outline" disabled={idx <= 0} onClick={() => onStatus(task.id, COLUMNS[idx - 1].key)} data-testid="task-move-left">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">{COLUMNS[idx]?.label}</span>
        <Button size="sm" variant="outline" disabled={idx >= COLUMNS.length - 1} onClick={() => onStatus(task.id, COLUMNS[idx + 1].key)} data-testid="task-move-right">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {total > 0 && (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 mb-1">
            <ListChecks className="h-4 w-4 text-indigo-600" />
            <span className="text-sm font-medium">Checklist ({done}/{total})</span>
          </div>
          {task.checklist.map((c) => <ChecklistRow key={c.id} item={c} meDone={isShared && myUid ? Boolean((c.done_by || {})[myUid]) : undefined} onToggle={(iid, d, p) => onChecklist(task.id, iid, d, p)} />)}
          {isShared && (
            <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="shared-completers">
              {completers.length === 0 ? (
                <span className="text-[11px] text-slate-400">Belum ada staff yang menyelesaikan</span>
              ) : (
                completers.map((d, i) => (
                  <Badge key={i} className="text-[10px] gap-1 bg-teal-100 text-teal-700 hover:bg-teal-100" data-testid={`completer-${i}`}>
                    <Check className="h-2.5 w-2.5" /> {d.name}{d.property_name ? ` · ${d.property_name}` : ""}
                  </Badge>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {proofItems.length > 0 && (
        <div className="rounded-lg border border-slate-200 p-3" data-testid="proof-gallery">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Images className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-medium">Proof gallery ({proofItems.length})</span>
            </div>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={() => setConfirmValidate(true)} data-testid="validate-proofs-btn">
                <ShieldCheck className="h-3.5 w-3.5" /> Validate & delete
              </Button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {proofItems.map((c) => (
              <button key={c.id} type="button" onClick={() => setPreview(c)} className="group relative" data-testid={`proof-thumb-${c.id}`}>
                <SelfieImage path={c.proof_path} className="h-20 w-full rounded-md object-cover ring-1 ring-slate-200 group-hover:ring-indigo-400" />
                <span className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-[10px] px-1 py-0.5 rounded-b-md truncate text-left">{c.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1" data-testid="task-copy-btn"
            onClick={async () => {
              try {
                await api.post(`/tasks/${task.id}/duplicate`);
                toast.success("Task duplicated (checklist & detail ikut tersalin)");
                onDuplicated();
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}>
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
        {(true) && (
          <CreateTaskDialog
            staff={staff}
            properties={properties}
            isAdmin={isAdmin}
            onCreated={onUpdated}
            task={task}
            trigger={<Button size="sm" variant="outline" className="flex-1" data-testid="task-edit-btn"><Pencil className="h-3.5 w-3.5" /> Edit</Button>}
          />
        )}
        {isAdmin && (
          <Button variant="destructive" size="sm" onClick={() => onDelete(task.id)} className="flex-1" data-testid="task-delete-btn">
            <Trash2 className="h-4 w-4" /> Delete task
          </Button>
        )}
      </div>
    </DialogContent>

    <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm">{preview?.text}</DialogTitle>
        </DialogHeader>
        {preview && <SelfieImage path={preview.proof_path} className="w-full max-h-[70vh] object-contain rounded-md" />}
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmValidate} onOpenChange={setConfirmValidate}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Validate {proofItems.length} proof photo(s)?</AlertDialogTitle>
          <AlertDialogDescription>
            All proof photos for this task will be permanently deleted from storage to save space. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="validate-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={validate} disabled={validating} data-testid="validate-confirm-btn">
            {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validate & delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function CreateTaskDialog({ staff, properties, isAdmin, onCreated, task = null, trigger = null, prefill = null, onTaskCreated = null }) {
  const [open, setOpen] = useState(false);
  const emptyForm = { title: "", description: "", source: "", property_id: "", assignee_id: "all", priority: "medium", due_date: "" };
  const [form, setForm] = useState(emptyForm);
  const [checklist, setChecklist] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!task;

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        source: task.source || "",
        property_id: task.property_id || "__all__",
        assignee_id: task.assignee_id || "all",
        priority: task.priority || "medium",
        due_date: task.due_date || "",
      });
      setChecklist([]);
    } else {
      setForm({ ...emptyForm, ...(prefill || {}) });
      setChecklist([]);
    }
  }, [open]);

  const propStaff = staff.filter((s) => (s.property_ids || []).includes(form.property_id));

  const addItem = () => setChecklist([...checklist, { text: "", requires_proof: false }]);
  const updItem = (i, patch) => setChecklist(checklist.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const rmItem = (i) => setChecklist(checklist.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) return toast.error("Select a property");
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        source: form.source || null,
        property_id: form.property_id === "__all__" ? null : form.property_id,
        assignee_id: form.assignee_id === "all" ? null : form.assignee_id,
        priority: form.priority,
        due_date: form.due_date || null,
      };
      if (isEdit) {
        await api.put(`/tasks/${task.id}`, payload);
        toast.success("Task updated");
      } else {
        const { data: created } = await api.post("/tasks", { ...payload, checklist: checklist.filter((c) => c.text.trim()) });
        toast.success("Task assigned");
        if (onTaskCreated) await onTaskCreated(created);
      }
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-task-btn"><Plus className="h-4 w-4" /> New task</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : "Assign new task"}</DialogTitle>
          <DialogDescription>{isEdit ? "Update task details. Checklist items are kept as-is." : "Create a task, assign it to a staff member, and add a checklist with optional screenshot proof."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Agent / Source</Label>
            <Select value={form.source || "__none__"} onValueChange={(v) => setForm({ ...form, source: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="task-source-select"><SelectValue placeholder="Select agent / source (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {TASK_SOURCES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Update Traveloka promo banner" data-testid="task-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="resize-none h-20" placeholder="Details / context" data-testid="task-desc-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Property</Label>
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v, assignee_id: "all" })}>
              <SelectTrigger data-testid="task-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>
                {(isAdmin || (isEdit && task && !task.property_id)) && <SelectItem value="__all__">All properties</SelectItem>}
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Assignee</Label>
              <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Whole property" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Whole property (all staff)</SelectItem>
                  {propStaff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Due date (optional)</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" />
          </div>

          {!isEdit && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Checklist</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-checklist-item-btn"><Plus className="h-3.5 w-3.5" /> Item</Button>
            </div>
            {checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={c.text} onChange={(e) => updItem(i, { text: e.target.value })} placeholder={`Item ${i + 1}`} className="flex-1" data-testid={`checklist-text-${i}`} />
                <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap cursor-pointer">
                  <Checkbox checked={c.requires_proof} onCheckedChange={(v) => updItem(i, { requires_proof: !!v })} data-testid={`checklist-proof-${i}`} />
                  <Camera className="h-3.5 w-3.5" />
                </label>
                <button type="button" onClick={() => rmItem(i)} className="text-slate-400 hover:text-rose-500" data-testid={`remove-checklist-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          )}

          <Button type="submit" className="w-full" disabled={saving} data-testid={isEdit ? "update-task-btn" : "save-task-btn"}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Assign task"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IdeasPanel({ staff, properties, isAdmin, flags, user, dark, reloadTasks }) {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);
  const sub = dark ? "text-slate-400" : "text-slate-500";

  const loadIdeas = useCallback(async () => {
    try {
      const { data } = await api.get("/ideas");
      setIdeas(data.ideas);
    } catch { toast.error("Failed to load ideas"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/ideas", form);
      toast.success("Idea terkirim");
      setForm({ title: "", description: "" });
      setOpen(false);
      loadIdeas();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delIdea = async (id) => {
    try { await api.delete(`/ideas/${id}`); toast.success("Idea dihapus"); loadIdeas(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canProcess = isAdmin || flags?.staff_create_tasks;

  return (
    <div data-testid="ideas-panel">
      <div className="flex items-center justify-between mb-4">
        <p className={`text-sm ${sub}`}>Kumpulkan ide dari tim — yang disetujui bisa diproses menjadi task.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="new-idea-btn"><Lightbulb className="h-4 w-4" /> New idea</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>New idea</DialogTitle>
              <DialogDescription>Ide perbaikan, promo, atau apa pun untuk tim. Bisa diproses menjadi task nanti.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Judul idea</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Misal: Paket long stay mingguan" data-testid="idea-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Deskripsi (optional)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detail idenya…" className="resize-none h-24" data-testid="idea-desc-input" />
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="idea-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim idea"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-amber-500" /></div>
      ) : ideas.length === 0 ? (
        <p className={`text-sm italic text-center py-16 ${sub}`} data-testid="ideas-empty">Belum ada idea — jadi yang pertama mengusulkan!</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ideas.map((idea) => (
            <div key={idea.id} className={`rounded-xl border p-4 flex flex-col gap-2 ${dark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`} data-testid={`idea-card-${idea.id}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className={`h-4 w-4 mt-0.5 flex-shrink-0 ${idea.status === "processed" ? "text-emerald-500" : "text-amber-500"}`} />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{idea.title}</p>
                  {idea.description && <p className={`text-xs mt-1 whitespace-pre-wrap ${sub}`}>{idea.description}</p>}
                </div>
                {(isAdmin || idea.created_by === user?.id) && (
                  <button onClick={() => delIdea(idea.id)} className="text-slate-500 hover:text-rose-400" data-testid={`idea-delete-${idea.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
                <span className={`text-[11px] ${sub}`}>{idea.created_by_name} · {new Date(idea.created_at).toLocaleDateString("id-ID")}</span>
                {idea.status === "processed" ? (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`} data-testid={`idea-status-${idea.id}`}>
                    Diproses → task{idea.processed_by ? ` oleh ${idea.processed_by}` : ""}
                  </span>
                ) : canProcess ? (
                  <CreateTaskDialog
                    staff={staff}
                    properties={properties}
                    isAdmin={isAdmin}
                    prefill={{ title: idea.title, description: idea.description }}
                    onCreated={() => { reloadTasks(); }}
                    onTaskCreated={async (t) => {
                      try {
                        await api.post(`/ideas/${idea.id}/process`, { task_id: t.id });
                        toast.success("Idea diproses menjadi task");
                        loadIdeas();
                      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                    }}
                    trigger={<Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`idea-process-${idea.id}`}><ArrowRightToLine className="h-3 w-3" /> Jadikan task</Button>}
                  />
                ) : (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`} data-testid={`idea-status-${idea.id}`}>Menunggu proses</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [flags, setFlags] = useState(null);
  const [view, setView] = useState("board");

  const load = useCallback(async () => {
    try {
      const reqs = [api.get("/tasks"), api.get("/settings/feature-access"), api.get("/properties")];
      if (isAdmin) reqs.push(api.get("/admin/staff"));
      const res = await Promise.all(reqs);
      setTasks(res[0].data.tasks);
      setFlags(res[1].data.flags);
      setProperties(res[2].data.properties);
      if (isAdmin) setStaff(res[3].data.staff);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status } : t)));
    try { await api.patch(`/tasks/${id}/status`, { status }); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  const toggleChecklist = async (taskId, itemId, done, proof) => {
    try {
      await api.patch(`/tasks/${taskId}/checklist/${itemId}`, { done, proof });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try { await api.delete(`/tasks/${id}`); toast.success("Task deleted"); setSelectedId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const validateProofs = async (id) => {
    try {
      const res = await api.post(`/tasks/${id}/proofs/validate`);
      toast.success(`${res.data.validated} proof photo(s) validated & deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const dark = isAdmin;
  const selectedTask = tasks.find((t) => t.id === selectedId);

  return (
    <div className={`min-h-screen ${dark ? "bg-[#1F1B16] dark-scroll text-slate-100" : "bg-slate-50"}`}>
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className={`font-display text-2xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>Tasks &amp; Idea</h1>
            <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>{isAdmin ? "Assign and monitor work across your team" : "Your assigned tasks and checklists"}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex rounded-lg border overflow-hidden ${dark ? "border-slate-700" : "border-slate-300"}`} data-testid="tasks-view-toggle">
              <button onClick={() => setView("board")} className={`px-3.5 h-9 text-xs font-medium ${view === "board" ? (dark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-100 text-indigo-700") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500")}`} data-testid="view-board">Board</button>
              <button onClick={() => setView("idea")} className={`px-3.5 h-9 text-xs font-medium border-l ${dark ? "border-slate-700" : "border-slate-300"} ${view === "idea" ? (dark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500")}`} data-testid="view-idea">
                <Lightbulb className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />Idea
              </button>
            </div>
            {view === "board" && (isAdmin || flags?.staff_create_tasks) && <CreateTaskDialog staff={staff} properties={properties} isAdmin={isAdmin} onCreated={load} />}
          </div>
        </div>

        {view === "idea" ? (
          <IdeasPanel staff={staff} properties={properties} isAdmin={isAdmin} flags={flags} user={user} dark={dark} reloadTasks={load} />
        ) : loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-indigo-500" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5" data-testid="kanban-board">
            {COLUMNS.map((col) => {
              const items = tasks.filter((t) => t.status === col.key);
              return (
                <div key={col.key} className={`rounded-xl p-3 ${dark ? "bg-slate-900 border border-slate-800" : "bg-slate-100/70 border border-slate-200"}`} data-testid={`column-${col.key}`}>
                  <div className="flex items-center justify-between px-1 mb-3">
                    <span className={`text-sm font-semibold ${dark ? "text-slate-200" : "text-slate-700"}`}>{col.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${dark ? "bg-slate-800 text-slate-400" : "bg-white text-slate-500"}`}>{items.length}</span>
                  </div>
                  <div className="space-y-2.5 min-h-[80px]">
                    {items.map((t) => {
                      const done = (t.checklist || []).filter((c) => (!t.property_id ? Boolean((c.done_by || {})[user?.id]) : c.done)).length;
                      const total = (t.checklist || []).length;
                      return (
                        <motion.div
                          key={t.id}
                          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                          onClick={() => setSelectedId(t.id)}
                          className={`rounded-lg p-3 cursor-pointer border transition-all hover:shadow-md ${dark ? "bg-slate-800 border-slate-700 hover:border-indigo-500" : "bg-white border-slate-200 hover:border-indigo-300"}`}
                          data-testid={`task-card-${t.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className={`text-sm font-medium ${dark ? "text-slate-100" : "text-slate-800"}`}>{t.title}</span>
                            <Flag className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${t.priority === "high" ? "text-rose-500" : t.priority === "medium" ? "text-sky-500" : "text-slate-400"}`} />
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`text-[11px] px-1.5 py-0.5 rounded ${dark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>{t.property_name}</span>
                            {t.source && <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${dark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-100 text-indigo-700"}`} data-testid={`task-source-${t.id}`}>{sourceLabel(t.source)}</span>}
                            {!t.property_id && (() => {
                              const u = Object.values((t.checklist || []).reduce((acc, c) => ({ ...acc, ...(c.done_by || {}) }), {}));
                              return (
                                <span className={`text-[10px] font-medium ${dark ? "text-teal-300" : "text-teal-600"}`} data-testid={`shared-done-${t.id}`}>
                                  {u.length ? `✓ ${u.length} staff: ${u.map((d) => d.name.split(" ")[0]).join(", ")}` : "✓ 0 staff selesai"}
                                </span>
                              );
                            })()}
                            {isAdmin && <span className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{t.assignee_name || "Whole property"}</span>}
                            {total > 0 && (
                              <span className={`text-xs flex items-center gap-1 ${done === total ? "text-emerald-500" : dark ? "text-slate-400" : "text-slate-500"}`}>
                                <ListChecks className="h-3 w-3" /> {done}/{total}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                    {items.length === 0 && <p className={`text-xs text-center py-4 ${dark ? "text-slate-600" : "text-slate-400"}`}>No tasks</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
          {selectedTask && (
            <TaskDialog task={selectedTask} isAdmin={isAdmin} onStatus={setStatus} onChecklist={toggleChecklist} onDelete={del} onValidate={validateProofs} myUid={user?.id} staff={staff} properties={properties} onUpdated={load} canCreate={isAdmin || flags?.staff_create_tasks} onDuplicated={() => { setSelectedId(null); load(); }} />
          )}
        </Dialog>
      </main>
    </div>
  );
}
